import { useContext, useEffect, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../shared/hooks/useAuthQuery';
import { useMutation } from 'convex/react';
import Markdown from 'react-markdown';
import { PlayIcon, CheckIcon } from '@heroicons/react/24/solid';
import { api } from '../../../convex/_generated/api';
import { BaseContext } from '../../../shared/base-context';
import { Id } from '../../../convex/_generated/dataModel';

export const IntroSlidesViewer = () => {
    const { convexUserId } = useContext(BaseContext);
    const slides = useQuery(api.intro_slides.listActive);
    const setIntroSlidesSeen = useMutation(api.user_metadata.setIntroSlidesSeen);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [nextIndex, setNextIndex] = useState<number | null>(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [skipping, setSkipping] = useState(false);

    // If query has loaded and there are no active slides, skip straight through
    useEffect(() => {
        if (slides !== undefined && slides.length === 0 && !skipping) {
            setSkipping(true);
            setIntroSlidesSeen({ user_id: convexUserId as Id<"users"> });
        }
    }, [slides, convexUserId, skipping, setIntroSlidesSeen]);

    const goToSlide = (target: number) => {
        if (isAnimating) return;
        setNextIndex(target);
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentIndex(target);
            setNextIndex(null);
            setIsAnimating(false);
        }, 300);
    };

    const handleNext = () => {
        if (currentIndex < (slides?.length ?? 0) - 1) {
            goToSlide(currentIndex + 1);
        } else {
            setIntroSlidesSeen({ user_id: convexUserId as Id<"users"> });
        }
    };

    // Still loading or no slides to show
    if (slides === undefined || slides.length === 0) {
        return null;
    }

    const displayIndex = nextIndex ?? currentIndex;
    const displaySlide = slides[displayIndex];
    const isLastSlide = displayIndex === slides.length - 1;

    return (
        <section className="fixed inset-0 z-20 bg-background animate-fade-in flex flex-col overflow-hidden">
            {/* Progress dots */}
            {slides.length > 1 && (
                <div className="flex justify-center gap-2 pt-6 pb-4">
                    {slides.map((_, idx) => (
                        <div
                            key={idx}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                idx === displayIndex
                                    ? 'w-8 bg-primary'
                                    : idx < displayIndex
                                      ? 'w-2 bg-primary/40'
                                      : 'w-2 bg-border'
                            }`}
                        />
                    ))}
                </div>
            )}

            {/* Title — fixed position, does not animate between slides */}
            <div className="intro-slide-title text-center px-4 mt-16 pb-6 max-w-2xl mx-auto w-full">
                <Markdown>{displaySlide.title}</Markdown>
            </div>

            {/* Slide body content — animated, fills remaining space */}
            <div className="flex-1 overflow-hidden relative px-4">
                <div className="max-w-2xl mx-auto w-full">
                    {isAnimating && nextIndex !== null ? (
                        <>
                            <div className="absolute inset-0 px-4 intro-slide-out-left">
                                <div className="max-w-2xl mx-auto w-full">
                                    <SlideBody slide={slides[currentIndex]} />
                                </div>
                            </div>
                            <div className="intro-slide-in-right">
                                <SlideBody slide={slides[nextIndex]} />
                            </div>
                        </>
                    ) : (
                        <SlideBody slide={slides[currentIndex]} />
                    )}
                </div>
            </div>

            {/* Navigation buttons */}
            <div className="flex flex-col items-center gap-3 pb-8 pt-4">
                {isLastSlide ? (
                    <button
                        onClick={handleNext}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-4 transition-all duration-200"
                    >
                        <CheckIcon className="size-6" />
                    </button>
                ) : (
                    <button
                        onClick={handleNext}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-4 transition-all duration-200"
                    >
                        <PlayIcon className="size-6" />
                    </button>
                )}
            </div>

            <style>{`
                @keyframes intro-slide-out-left {
                    from { opacity: 1; transform: translateX(0); }
                    to { opacity: 0; transform: translateX(-100%); }
                }
                @keyframes intro-slide-in-right {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes intro-slide-out-right {
                    from { opacity: 1; transform: translateX(0); }
                    to { opacity: 0; transform: translateX(100%); }
                }
                @keyframes intro-slide-in-left {
                    from { opacity: 0; transform: translateX(-100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .intro-slide-out-left {
                    animation: intro-slide-out-left 0.3s ease-in-out forwards;
                }
                .intro-slide-in-right {
                    animation: intro-slide-in-right 0.3s ease-in-out forwards;
                }
                .intro-slide-out-right {
                    animation: intro-slide-out-right 0.3s ease-in-out forwards;
                }
                .intro-slide-in-left {
                    animation: intro-slide-in-left 0.3s ease-in-out forwards;
                }
                .intro-slide-title h1,
                .intro-slide-title h2,
                .intro-slide-title h3,
                .intro-slide-title p {
                    font-size: 1.875rem;
                    font-weight: 600;
                    color: var(--foreground);
                    margin: 0;
                }
                .intro-slide-body p {
                    font-size: 1.125rem;
                    line-height: 1.75;
                    color: var(--muted-foreground);
                    margin-bottom: 0.75rem;
                }
                .intro-slide-body p:last-child {
                    margin-bottom: 0;
                }
                .intro-slide-body strong {
                    color: var(--foreground);
                    font-weight: 600;
                }
                .intro-slide-body em {
                    font-style: italic;
                }
                .intro-slide-body ul,
                .intro-slide-body ol {
                    padding-left: 1.5rem;
                    margin-bottom: 0.75rem;
                    color: var(--muted-foreground);
                    font-size: 1.125rem;
                    line-height: 1.75;
                }
                .intro-slide-body li {
                    margin-bottom: 0.25rem;
                }
                .intro-slide-body a {
                    color: var(--primary);
                    text-decoration: underline;
                }
            `}</style>
        </section>
    );
};

const SlideBody = ({ slide }: { slide: { title: string; body: string; image_id?: Id<"_storage">; cta_text?: string } }) => {
    const imageUrl = useQuery(
        api.intro_slides.getImageUrl,
        slide?.image_id ? { image_id: slide.image_id } : 'skip'
    );

    if (!slide) return null;

    const bodyContent = (
        <div className={`w-full intro-slide-body ${imageUrl ? 'text-center md:text-left' : 'text-left'}`}>
            <Markdown>{slide.body}</Markdown>
        </div>
    );

    if (imageUrl) {
        return (
            <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:gap-8">
                <div className="w-36 h-36 md:w-48 md:h-48 flex-shrink-0 flex items-center justify-center">
                    <img
                        src={imageUrl}
                        alt={slide.title}
                        className="max-w-full max-h-full object-contain rounded-2xl"
                    />
                </div>
                <div className="flex-1 min-w-0">
                    {bodyContent}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center">
            {bodyContent}
        </div>
    );
};
